import React, { useState } from 'react'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { cn } from '../../../../lib/utils'
import PerformanceDashboardService from '../../../../services/dashboards/PerformanceDashboard.service'
import EpochMonthService from '../../../../services/EpochMonth.service'
import KpiHelpIcon from './KpiHelpIcon'
import type { InventoryHealthRow, SellThroughRow } from '../ClientDashboardV3Performance'

type ClassTab = 'Buying Opportunity' | 'Fastest Sell-Through' | 'Slowest Sell-Through'
type ValueMode = 'Retail' | 'Cost'

export interface BuyingRowSummary {
  key: string;
  classLabel: string;
  category: string;
  location: string;
  salesLabel: string;
  salesPlanLabel: string;
  markdownsLabel: string;
  inventoryLabel: string;
  inventoryTrendLabel: string;
  inventoryPlanLabel: string;
  inventoryTrendPositive: boolean;
  inventoryPlanPositive: boolean;
  onOrderRetailLabel: string;
  onOrderCostLabel: string;
  otbRetailLabel: string;
  otbCostLabel: string;
  otbRetailPositive: boolean;
  otbCostPositive: boolean;
  otbPct: number;
  actionLabel: string;
  actionClassName: string;
}

export interface SellThroughRowSummary {
  key: string;
  classLabel: string;
  category: string;
  location: string;
  salesLabel: string;
  inflowLabel: string;
  beginningInventoryLabel: string;
  mmuLabel: string;
  mmuTone: string;
  sellThroughLabel: string;
  sellThroughTone: string;
  compToLyLabel: string;
  compToLyUp: boolean;
  goalLabel: string;
}

function formatPercent(value: number, digits = 1) {
  return `${value.toFixed(digits)}%`
}

function formatSignedPercent(value: number) {
  const prefix = value >= 0 ? '+' : ''
  return `${prefix}${value.toFixed(1)}%`
}

function YoYBadge({ value, up }: { value: string; up: boolean }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium', up ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
      {up ? <ArrowUp className="h-2.5 w-2.5" /> : <ArrowDown className="h-2.5 w-2.5" />}
      {value}
    </span>
  )
}

export function summarizePerformanceBuyingRows(rows: InventoryHealthRow[], limit = 10): BuyingRowSummary[] {
  const maxOtb = Math.max(...rows.map((row) => Math.abs(Math.max(Number(row.otbRetail) || 0, Number(row.otbCost) || 0))), 1)

  return rows.slice(0, limit).map((row) => {
    const inventoryRetail = Number(row.inventoryRetail) || 0
    const inventoryTrendRetail = Number(row.inventoryTrendRetail) || 0
    const inventoryPlanRetail = Number(row.inventoryPlanRetail) || 0
    const otbRetail = Number(row.otbRetail) || 0
    const otbCost = Number(row.otbCost) || 0
    const otbMagnitude = Math.max(Math.abs(otbRetail), Math.abs(otbCost))
    const planGap = inventoryPlanRetail - inventoryRetail
    const actionLabel = planGap > 0 ? 'Chase' : 'Watch'

    return {
      key: `${row.locationCode || 'all'}-${row.classCode}`,
      classLabel: `${row.classCode} - ${row.className}`,
      category: row.category,
      location: row.locationCode ? `${row.locationCode}${row.locationName ? ` - ${row.locationName}` : ''}` : 'All locations',
      salesLabel: PerformanceDashboardService.moneyFormat(row.salesRetail || 0, true),
      salesPlanLabel: PerformanceDashboardService.moneyFormat(Number(row.salesPlanRetail) || Number((row as any).salesPlan) || Number((row as any).salesPlanRM) || 0, true),
      markdownsLabel: PerformanceDashboardService.moneyFormat(row.markdownsRetail || 0, true),
      inventoryLabel: PerformanceDashboardService.moneyFormat(inventoryRetail, true),
      inventoryTrendLabel: PerformanceDashboardService.moneyFormat(inventoryTrendRetail, true),
      inventoryPlanLabel: PerformanceDashboardService.moneyFormat(inventoryPlanRetail, true),
      inventoryTrendPositive: inventoryTrendRetail >= inventoryRetail,
      inventoryPlanPositive: inventoryPlanRetail >= inventoryRetail,
      onOrderRetailLabel: PerformanceDashboardService.moneyFormat(row.onOrderRetail || 0, true),
      onOrderCostLabel: PerformanceDashboardService.moneyFormat(row.onOrderCost || 0, true),
      otbRetailLabel: PerformanceDashboardService.moneyFormat(otbRetail, true),
      otbCostLabel: PerformanceDashboardService.moneyFormat(otbCost, true),
      otbRetailPositive: otbRetail >= 0,
      otbCostPositive: otbCost >= 0,
      otbPct: Math.max(8, Math.round((otbMagnitude / maxOtb) * 100)),
      actionLabel,
      actionClassName: planGap > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700',
    }
  })
}

export function summarizePerformanceSellThroughRows(rows: SellThroughRow[], limit = 10): SellThroughRowSummary[] {
  return rows.slice(0, limit).map((row) => {
    const mmu = (row.mmuPercent || 0) * 100
    const sellThrough = row.sellThroughRate || 0
    const compToLy = row.compToLyPercent || 0

    return {
      key: `${row.locationCode || 'all'}-${row.classCode}`,
      classLabel: `${row.classCode} - ${row.className}`,
      category: row.category,
      location: row.locationCode ? `${row.locationCode}${row.locationName ? ` - ${row.locationName}` : ''}` : 'All locations',
      salesLabel: PerformanceDashboardService.moneyFormat(row.sales || 0, true),
      inflowLabel: PerformanceDashboardService.moneyFormat(row.inflowRetail || 0, true),
      beginningInventoryLabel: PerformanceDashboardService.moneyFormat(row.beginningInventory || 0, true),
      mmuLabel: formatPercent(mmu),
      mmuTone: mmu > 15 ? 'text-red-500' : mmu > 10 ? 'text-yellow-500' : 'text-emerald-500',
      sellThroughLabel: formatPercent(sellThrough),
      sellThroughTone: sellThrough > 80 ? 'text-emerald-500' : sellThrough > 60 ? 'text-yellow-500' : 'text-red-500',
      compToLyLabel: formatSignedPercent(compToLy),
      compToLyUp: compToLy >= 0,
      goalLabel: formatPercent(row.sellThroughGoal || 0),
    }
  })
}

export function MerchandiseInsightsSection({
  loading,
  inventoryHealthData,
  fastestSellThroughData,
  slowestSellThroughData,
  tableLimit,
  onTableLimitChange,
  beginningPeriod,
  onBeginningPeriodChange,
  beginningPeriodOptions,
  endingPeriodLabel,
  periodLabel,
}: {
  loading: boolean;
  inventoryHealthData: InventoryHealthRow[];
  fastestSellThroughData: SellThroughRow[];
  slowestSellThroughData: SellThroughRow[];
  tableLimit: number;
  onTableLimitChange: (nextLimit: number) => void;
  beginningPeriod: number;
  onBeginningPeriodChange: (nextBeginningPeriod: number) => void;
  beginningPeriodOptions: Array<{ value: number; label: string }>;
  endingPeriodLabel: string;
  periodLabel: string;
}) {
  const [classTab, setClassTab] = useState<ClassTab>('Buying Opportunity')
  const [unitMode, setUnitMode] = useState<ValueMode>('Cost')
  const actualsPeriod = React.useMemo(() => {
    const todayEpoch = EpochMonthService.getTodayEpochMonth()
    const diff = todayEpoch - beginningPeriod
    if (diff === 0) return 'MTD'
    if (diff === 1) return 'Last Month'
    if (diff === 2) return 'Last 3M'
    return 'Last 12M'
  }, [beginningPeriod])

  const buyingRows: BuyingRowSummary[] = React.useMemo(() => summarizePerformanceBuyingRows(inventoryHealthData, tableLimit), [inventoryHealthData, tableLimit])
  const fastestRows: SellThroughRowSummary[] = React.useMemo(() => summarizePerformanceSellThroughRows(fastestSellThroughData, tableLimit), [fastestSellThroughData, tableLimit])
  const slowestRows: SellThroughRowSummary[] = React.useMemo(() => summarizePerformanceSellThroughRows(slowestSellThroughData, tableLimit), [slowestSellThroughData, tableLimit])
  const activeBeginningPeriodLabel = beginningPeriodOptions.find((option) => option.value === beginningPeriod)?.label || 'Unknown'
  const sellThroughRows: SellThroughRowSummary[] = classTab === 'Fastest Sell-Through' ? fastestRows : slowestRows

  const handlePillClick = (p: string) => {
    const todayEpoch = EpochMonthService.getTodayEpochMonth()
    let targetMonth = todayEpoch

    if (p === 'MTD') {
      targetMonth = todayEpoch
    } else if (p === 'Last Month') {
      targetMonth = Math.max(todayEpoch - 1, 0)
    } else if (p === 'Last 3M') {
      targetMonth = Math.max(todayEpoch - 2, 0)
    } else if (p === 'Last 12M') {
      targetMonth = Math.max(todayEpoch - 11, 0)
    }

    onBeginningPeriodChange(targetMonth)
  }

  const renderSellThroughRows = (rows: SellThroughRowSummary[]) => rows.map((row) => (
    <tr key={row.key} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="text-[14px] font-medium text-gray-900">{row.classLabel}</div>
        <div className="text-[12px] text-gray-500">{row.category}</div>
        {row.location !== 'All locations' && <div className="text-[12px] text-gray-500 opacity-60 mt-1">[{row.location}]</div>}
      </td>
      <td className="px-4 py-3 text-right font-mono text-[14px] text-gray-900">{row.salesLabel}</td>
      <td className="px-4 py-3 text-right font-mono text-[14px] text-gray-900">{row.inflowLabel}</td>
      <td className="px-4 py-3 text-right font-mono text-[14px] text-gray-900">{row.beginningInventoryLabel}</td>
      <td className={cn('px-4 py-3 text-center text-[14px] font-semibold', row.mmuTone)}>{row.mmuLabel}</td>
      <td className={cn('px-4 py-3 text-center text-[14px] font-semibold', row.sellThroughTone)}>{row.sellThroughLabel}</td>
      <td className="px-4 py-3 text-center"><YoYBadge value={row.compToLyLabel} up={row.compToLyUp} /></td>
      <td className="px-4 py-3 text-center text-[14px] font-semibold text-gray-600">{row.goalLabel}</td>
    </tr>
  ))

  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* Top Header Row - Exact Figma Ditto Copy */}
      <div className="flex items-center justify-between gap-4 border-b border-[#EAECF0] px-6 py-4">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-[18px] font-bold tracking-tight text-[#101828]">Merchandise Insights</h3>
          <p className="text-[14px] text-[#667085]">Performance across your classes · {periodLabel}</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Value Mode (Retail / Cost) toggle */}
          {classTab === 'Buying Opportunity' && (
            <div className="flex items-center gap-0.5 rounded-lg bg-[#F2F4F7] p-[3px]">
              {(['Retail', 'Cost'] as ValueMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setUnitMode(mode)}
                  className={cn(
                    'rounded-md px-3 py-1 text-xs font-semibold transition-all',
                    unitMode === mode ? 'bg-purple-700 text-white shadow-xs' : 'text-[#667085] hover:text-[#344054]'
                  )}
                >
                  {mode}
                </button>
              ))}
            </div>
          )}

          {/* Top Limit Dropdown */}
          <div className="relative">
            <select
              aria-label="Merchandise Insights item limit"
              value={tableLimit}
              onChange={(event) => onTableLimitChange(Number(event.target.value))}
              className="flex h-9 shrink-0 items-center rounded-lg border border-[#D0D5DD] bg-white px-3 pr-8 text-xs font-semibold text-[#344054] shadow-xs focus:outline-none focus:ring-2 focus:ring-[#7F56D9]/20"
            >
              <option value={5}>Top 5</option>
              <option value={10}>Top 10</option>
              <option value={15}>Top 15</option>
              <option value={20}>Top 20</option>
            </select>
          </div>
        </div>
      </div>

      {/* Second Row - Tabs & Time Controls */}
      <div className="flex flex-col gap-3 border-t border-[#EAECF0] px-6 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1 rounded-xl bg-[#F8FAFC] p-[4px] border border-[#F1F5F9]">
          {(['Buying Opportunity', 'Fastest Sell-Through', 'Slowest Sell-Through'] as ClassTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setClassTab(tab)}
              className={cn(
                'whitespace-nowrap rounded-lg px-4 py-2 text-[14px] font-semibold transition-all',
                classTab === tab ? 'bg-white text-[#0F172A] shadow-xs' : 'text-[#64748B] hover:text-[#0F172A]'
              )}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:justify-end">
          {/* Date Range Pills for Actuals */}
          <div aria-label="Merchandise Insights range pills" className="flex items-center gap-1 rounded-xl bg-[#F8FAFC] p-[3px] border border-[#F1F5F9]">
            {['MTD', 'Last Month', 'Last 3M', 'Last 12M'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => handlePillClick(p)}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-xs font-semibold transition-all',
                  actualsPeriod === p ? 'bg-[#F4EBFF] text-[#6941C6] shadow-2xs' : 'text-[#64748B] hover:text-[#0F172A]'
                )}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Monthly Dropdown (Visible across all tabs per QA requirement) */}
          <div className="flex items-center gap-1.5 rounded-lg border border-[#D0D5DD] bg-white px-3 py-1.5 shadow-2xs">
            <span className="text-xs font-medium text-gray-500">Months Forward for OTB</span>
            <select
              aria-label="Merchandise Insights beginning period"
              value={beginningPeriod}
              onChange={(event) => onBeginningPeriodChange(Number(event.target.value))}
              className="h-6 rounded-md border-0 bg-transparent pr-4 text-xs font-semibold text-[#344054] focus:outline-none"
            >
              {beginningPeriodOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1180px] border-collapse text-left">
          {classTab === 'Buying Opportunity' ? (
            <>
              <thead>
                <tr className="border-b border-t border-[#EAECF0] bg-[#F8FAFC]">
                  <th className="whitespace-nowrap px-4 py-3 text-[12px] font-semibold text-[#344054]">Class <span className="text-[#98A2B3]">↕</span></th>
                  <th className="whitespace-nowrap px-4 py-3 text-right text-[12px] font-semibold text-[#344054]">Sales <span className="text-[#98A2B3]">↓</span></th>
                  <th className="whitespace-nowrap px-4 py-3 text-right text-[12px] font-semibold text-[#344054]">Sales Plan <span className="text-[#98A2B3]">↓</span></th>
                  <th className="whitespace-nowrap px-4 py-3 text-right text-[12px] font-semibold text-[#344054]">Markdowns <span className="text-[#98A2B3]">↓</span></th>
                  <th className="whitespace-nowrap border-l border-[#EAECF0] px-4 py-3 text-right text-[12px] font-semibold text-[#344054]">Inventory <span className="text-[#98A2B3]">↓</span></th>
                  <th className="whitespace-nowrap px-4 py-3 text-right text-[12px] font-semibold text-[#344054]">
                    <span className="inline-flex items-center justify-end gap-1">
                      Trended Inventory <KpiHelpIcon tooltipKey="inventoryAtRetail" /> <span className="text-[#98A2B3]">↕</span>
                    </span>
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-right text-[12px] font-semibold text-[#344054]">
                    <span className="inline-flex items-center justify-end gap-1">
                      vs Plan <KpiHelpIcon tooltipKey="inventoryAtRetail" /> <span className="text-[#98A2B3]">↕</span>
                    </span>
                  </th>
                  <th className="whitespace-nowrap border-l border-[#EAECF0] px-4 py-3 text-right text-[12px] font-semibold text-[#344054]">On Order ({unitMode}) <span className="text-[#98A2B3]">↓</span></th>
                  <th className="whitespace-nowrap px-4 py-3 text-right text-[12px] font-semibold text-[#344054]">
                    <span className="inline-flex items-center justify-end gap-1">
                      Open to Buy Trend ({unitMode}) <KpiHelpIcon tooltipKey="openToBuy" /> <span className="text-[#98A2B3]">↕</span>
                    </span>
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-center text-[12px] font-semibold text-[#344054]">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EAECF0]">
                {loading ? (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-[#667085]">Loading inventory health data...</td></tr>
                ) : buyingRows.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-[#667085]">No data available</td></tr>
                ) : buyingRows.map((row) => (
                  <tr key={row.key} className="hover:bg-[#F8FAFC] transition-colors">
                    <td className="px-4 py-3.5">
                      <div className="text-[14px] font-bold text-[#101828]">{row.classLabel}</div>
                      <div className="text-[12px] text-[#667085] mt-0.5">{row.category}</div>
                      {row.location !== 'All locations' && <div className="text-[12px] text-[#98A2B3] mt-0.5">[{row.location}]</div>}
                    </td>
                    <td className="px-4 py-3.5 text-right font-medium text-[14px] text-[#101828]">{row.salesLabel}</td>
                    <td className="px-4 py-3.5 text-right font-medium text-[14px] text-[#101828]">{row.salesPlanLabel}</td>
                    <td className="px-4 py-3.5 text-right font-medium text-[14px] text-[#101828]">{row.markdownsLabel}</td>
                    <td className="border-l border-[#EAECF0] px-4 py-3.5 text-right font-medium text-[14px] text-[#101828]">{row.inventoryLabel}</td>
                    <td className="px-4 py-3.5 text-right text-[14px] font-semibold">
                      <span className={row.inventoryTrendPositive ? 'text-emerald-600' : 'text-red-600'}>{row.inventoryTrendLabel}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right text-[14px] font-semibold">
                      <span className={row.inventoryPlanPositive ? 'text-emerald-600' : 'text-red-600'}>{row.inventoryPlanLabel}</span>
                    </td>
                    <td className="border-l border-[#EAECF0] px-4 py-3.5 text-right font-medium text-[14px] text-[#101828]">{unitMode === 'Cost' ? row.onOrderCostLabel : row.onOrderRetailLabel}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-end gap-2.5">
                        <span className={cn('text-[14px] font-semibold text-emerald-600', unitMode === 'Cost' ? (row.otbCostPositive ? 'text-emerald-600' : 'text-red-600') : (row.otbRetailPositive ? 'text-emerald-600' : 'text-red-600'))}>
                          {unitMode === 'Cost' ? row.otbCostLabel : row.otbRetailLabel}
                        </span>
                        <span className="h-1.5 w-[56px] overflow-hidden rounded-full bg-[#EAECF0]">
                          <span className={cn('block h-full rounded-full', unitMode === 'Cost' ? (row.otbCostPositive ? 'bg-[#12B76A]' : 'bg-[#D92D20]') : (row.otbRetailPositive ? 'bg-[#12B76A]' : 'bg-[#D92D20]'))} style={{ width: `${row.otbPct}%` }} />
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold', row.actionClassName)}>
                        {row.actionLabel}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </>
          ) : (
            <>
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#344054]">Class</th>
                  <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#344054]">Sales</th>
                  <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#344054]">Inflow Retail</th>
                  <th className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#344054]">Beginning Inventory<div className="mt-0.5 text-[11px] font-normal text-gray-500">From {activeBeginningPeriodLabel}</div></th>
                  <th className="text-center px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#344054]">MMU %</th>
                  <th className="text-center px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#344054]">Sell-Through</th>
                  <th className="text-center px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#344054]">Comp to LY</th>
                  <th className="text-center px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#344054]">Sell-Through Goal</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">Loading sell-through data...</td></tr>
                ) : sellThroughRows.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">No data available</td></tr>
                ) : renderSellThroughRows(sellThroughRows)}
              </tbody>
            </>
          )}
        </table>
      </div>
    </section>
  )
}

